import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { Loader2, Eye, EyeOff, Truck, PackageOpen } from 'lucide-react';
import { toast } from 'sonner';

/**
 * WorkflowTransitionModal
 * - action: "deliver" (SALIDA -> ENTREGADO), "return" (ENTREGADO -> RETORNO)
 * - Both authenticate against any active SYSTEM USER (username + password)
 * - The final transition (RETORNO -> COMPLETADO) is NOT handled here. It happens
 *   automatically when a retorno movement is created via the legacy ReturnModal.
 */
const ACTION_LABELS = {
  receipt: {
    title: 'Confirmar recibo del equipo',
    description: 'Confirma con tu usuario y contraseña que recibiste el equipo del almacén.',
    button: 'Confirmar recibo',
    icon: PackageOpen,
    color: 'bg-emerald-600 hover:bg-emerald-700',
    endpoint: 'confirm-receipt',
    authType: 'user',
  },
  deliver: {
    title: 'Marcar como Entregado',
    description: 'Confirma con tu usuario y contraseña que el equipo fue entregado al cliente.',
    button: 'Confirmar entrega',
    icon: Truck,
    color: 'bg-blue-500 hover:bg-blue-600',
    endpoint: 'transition/deliver',
    authType: 'user',
  },
  return: {
    title: 'Marcar retorno — recibimos el equipo',
    description: 'Confirma con tu usuario y contraseña que el equipo regresó del evento/préstamo (lo recibimos de vuelta). Después registra el retorno.',
    button: 'Confirmar retorno',
    icon: PackageOpen,
    color: 'bg-amber-500 hover:bg-amber-600',
    endpoint: 'transition/return',
    authType: 'user',
  },
  return_receipt: {
    title: 'Confirmar entrega al almacén',
    description: 'Quien entrega el equipo al almacén confirma con SU usuario y contraseña. Después, el responsable registra el retorno para completar el movimiento.',
    button: 'Confirmar entrega al almacén',
    icon: PackageOpen,
    color: 'bg-emerald-600 hover:bg-emerald-700',
    endpoint: 'confirm-return-receipt',
    authType: 'user',
  },
};

const WorkflowTransitionModal = ({
  open,
  onOpenChange,
  action,
  movement,
  responsibles = [],
  onSuccess,
  api,
}) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [responsibleId, setResponsibleId] = useState('');
  const [notes, setNotes] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  const config = ACTION_LABELS[action] || ACTION_LABELS.deliver;
  const Icon = config.icon;
  const pending = movement?.pending_confirmation || null;
  const consentText = action === 'receipt'
    ? (pending?.consent_text || 'Confirmo que yo saqué el equipo del inventario y me hago responsable de este.')
    : action === 'return_receipt'
      ? (pending?.consent_text || 'Entregué el equipo al inventario.')
      : null;
  const targetName = pending?.target_user_name || null;

  useEffect(() => {
    if (!open) return;
    setUsername('');
    setPassword('');
    setNotes('');
    setShowPassword(false);
    // Pre-select the original responsible if verifying
    if (action === 'verify' && movement?.responsible_id) {
      setResponsibleId(movement.responsible_id);
    } else {
      setResponsibleId('');
    }
  }, [open, action, movement]);

  const handleSubmit = async () => {
    if (!movement?.id) return;

    if (config.authType === 'user') {
      if (!username.trim() || !password.trim()) {
        toast.error('Usuario y contraseña son obligatorios');
        return;
      }
    } else {
      if (!responsibleId || !password.trim()) {
        toast.error('Selecciona un responsable e ingresa su contraseña');
        return;
      }
    }

    try {
      setLoading(true);
      const payload =
        config.authType === 'user'
          ? { username: username.trim(), password, notes: notes.trim() || null }
          : { responsible_id: responsibleId, password, notes: notes.trim() || null };

      const res = await api.post(`/movements/${movement.id}/${config.endpoint}`, payload);
      toast.success(`Movimiento actualizado a "${res.data.workflow_status}"`);
      onOpenChange(false);
      if (onSuccess) onSuccess(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Error al ejecutar la transición';
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]" data-testid={`workflow-modal-${action}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon className="h-5 w-5 text-primary" />
            {config.title}
          </DialogTitle>
          <DialogDescription>{config.description}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {movement && (
            <div className="rounded-md bg-muted/40 border p-3 text-xs space-y-1">
              <div><strong>Evento:</strong> {movement.event_name || '—'}</div>
              <div><strong>Cliente:</strong> {movement.client_name || '—'}</div>
              {movement.control_number && (
                <div><strong>No. Control:</strong> {movement.control_number}</div>
              )}
              <div>
                <strong>Estado actual:</strong>{' '}
                <span className="uppercase tracking-wide">{movement.workflow_status || '—'}</span>
              </div>
            </div>
          )}

          {consentText && (
            <div className="rounded-md border-2 border-primary/40 bg-primary/5 p-3" data-testid="workflow-consent-box">
              <p className="text-sm font-semibold text-primary">{consentText}</p>
              {targetName && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Debe confirmar: <strong>{targetName}</strong> (o un administrador)
                </p>
              )}
            </div>
          )}

          {config.authType === 'user' ? (
            <div className="space-y-2">
              <Label htmlFor="wf-username">Usuario</Label>
              <Input
                id="wf-username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="usuario o email"
                autoComplete="username"
                data-testid="workflow-username-input"
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label>Responsable de bodega</Label>
              <Select value={responsibleId} onValueChange={setResponsibleId}>
                <SelectTrigger data-testid="workflow-responsible-select">
                  <SelectValue placeholder="Selecciona un responsable" />
                </SelectTrigger>
                <SelectContent>
                  {responsibles.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="wf-password">Contraseña</Label>
            <div className="relative">
              <Input
                id="wf-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                data-testid="workflow-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="wf-notes">Notas (opcional)</Label>
            <Textarea
              id="wf-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observaciones de la transición"
              rows={2}
              data-testid="workflow-notes-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancelar
          </Button>
          <Button
            className={`text-white ${config.color}`}
            onClick={handleSubmit}
            disabled={loading}
            data-testid="workflow-submit-btn"
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {config.button}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default WorkflowTransitionModal;
